<div class="event-registration-container">
  <!-- Header -->
  <div class="header">
    <h1>{{ 'eventRegistration.title' | translate }}</h1>
    <button class="btn-back" (click)="router.navigate(['/shows'])">
      <i class="fas fa-arrow-left"></i> {{ 'eventRegistration.backToEvents' | translate }}
    </button>
  </div>

  <!-- Loading State -->
  <div *ngIf="isLoadingEvent || isLoadingClasses || isLoadingServices" class="loading-container">
    <div class="spinner"></div>
    <p>{{ 'eventRegistration.loadingEventDetails' | translate }}</p>
  </div>

  <!-- Event Details -->
  <div *ngIf="event && !isLoadingEvent" class="event-details">
    <div class="event-header">
      <img *ngIf="event.image_url" [src]="event.image_url" alt="{{ event.event_name }}" class="event-image" />
      <div class="event-info">
        <h2>{{ event.event_name }}</h2>
        <div class="event-meta">
          <div class="meta-item" *ngIf="event.company">
            <i class="fas fa-building"></i>
            <span class="organized-by">{{ 'eventRegistration.organizedBy' | translate }} <strong>{{ event.company.company_name }}</strong></span>
          </div>
          <div class="meta-item">
            <i class="fas fa-calendar"></i>
            <span>{{ event.start_date | date:'mediumDate' }} - {{ event.end_date | date:'mediumDate' }}</span>
          </div>
          <div class="meta-item">
            <i class="fas fa-map-marker-alt"></i>
            <a [href]="getGoogleMapsLink(event)" target="_blank" class="address-link">
              {{ event.address }}, {{ event.city }}, {{ event.country }}
            </a>
          </div>
          <div class="meta-item">
            <i class="fas fa-clock"></i>
            <span>{{ 'eventRegistration.registrationDeadline' | translate }}: {{ event.registration_deadline | date:'mediumDate' }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Event Description Card -->
    <div class="event-description-card" *ngIf="event.event_description">
      <h3>{{ 'eventRegistration.aboutThisEvent' | translate }}</h3>
      <div
        class="event-description-content"
        [class.collapsed]="!descriptionExpanded"
        [innerHTML]="event.event_description">
      </div>
      <button class="btn-expand-description" (click)="toggleDescription()">
        <span *ngIf="!descriptionExpanded">
          <i class="fas fa-chevron-down"></i> {{ 'eventRegistration.showMore' | translate }}
        </span>
        <span *ngIf="descriptionExpanded">
          <i class="fas fa-chevron-up"></i> {{ 'eventRegistration.showLess' | translate }}
        </span>
      </button>
    </div>
  </div>

  <!-- Main Content -->
  <div class="content-grid">
    <!-- Left Column: Classes/Packages and Services -->
    <div class="left-column">
      <!-- Training Packages (for training events) -->
      <div class="section" *ngIf="isTrainingEvent">
        <h3>{{ 'eventRegistration.trainingPackages' | translate }}</h3>
        <p class="section-description">{{ 'eventRegistration.trainingPackagesDesc' | translate }}</p>

        <div *ngIf="isLoadingPackages" class="loading-state">
          <div class="spinner-sm"></div>
          <p>{{ 'eventRegistration.loadingPackages' | translate }}</p>
        </div>

        <div *ngIf="!isLoadingPackages && trainingPackages.length === 0" class="empty-state">
          <p>{{ 'eventRegistration.noPackages' | translate }}</p>
        </div>

        <div class="package-list">
          <div *ngFor="let pkg of trainingPackages" class="package-card">
            <div class="package-card-content">
              <div class="package-info">
                <div class="package-header">
                  <h4>{{ pkg.package_name }}</h4>
                  <span class="session-type-badge" [ngClass]="'type-' + pkg.session_type">
                    {{ getSessionTypeLabel(pkg.session_type) }}
                  </span>
                  <span class="badge" [ngClass]="getPackageAvailabilityClass(pkg)">
                    {{ getPackageAvailabilityLabel(pkg) }}
                  </span>
                </div>
                <p class="package-description" *ngIf="pkg.package_description">{{ pkg.package_description }}</p>

                <div class="package-details">
                  <div class="detail-item">
                    <i class="fas fa-calendar-check"></i>
                    <span>{{ pkg.session_count }} {{ pkg.session_count > 1 ? ('eventRegistration.sessions' | translate) : ('eventRegistration.session' | translate) }}</span>
                  </div>
                </div>
              </div>

              <div class="package-actions">
                <div class="price-row">
                  <div class="package-price">
                    @if (!pkg.price_cents || pkg.price_cents === 0) {
                      <span class="free-badge">{{ 'eventRegistration.free' | translate }}</span>
                    } @else if (hasMemberDiscount('package', pkg.package_id)) {
                      <span class="original-price">{{ pkg.price_cents / 100 }} {{ pkg.currency }}</span>
                      <span class="discounted-price">{{ getEffectivePrice('package', pkg.package_id, pkg.price_cents) / 100 }} {{ pkg.currency }}</span>
                      <span class="savings-badge">{{ getSavingsPercent('package', pkg.package_id) }}% {{ 'eventRegistration.off' | translate }}</span>
                    } @else {
                      {{ pkg.price_cents / 100 }} {{ pkg.currency }}
                    }
                  </div>
                  <button
                    class="btn-add-package"
                    (click)="addPackageRegistration(pkg)"
                    [disabled]="isPackageFull(pkg)">
                    <i class="fas fa-plus"></i>
                    {{ isPackageFull(pkg) ? ('eventRegistration.full' | translate) : ('eventRegistration.add' | translate) }}
                  </button>
                </div>
                @if (canGetMemberDiscount('package', pkg.package_id)) {
                  <div class="member-offer">
                    <span class="member-price">{{ getBestMemberPrice('package', pkg.package_id) }} {{ pkg.currency }} {{ 'eventRegistration.forMembers' | translate }} ({{ getBestMemberSavings('package', pkg.package_id) }}% {{ 'eventRegistration.off' | translate }})</span>
                    <button class="btn-become-member" (click)="openMembershipPurchaseModal('package', pkg.package_id, pkg.package_name, pkg.price_cents / 100, pkg.currency || event?.default_currency || 'NOK'); $event.stopPropagation()">
                      {{ 'eventRegistration.becomeMember' | translate }}
                    </button>
                  </div>
                }
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Event Classes (for competition events only) -->
      <div class="section" *ngIf="isCompetitionEvent">
        <h3>{{ 'eventRegistration.eventClasses' | translate }}</h3>
        <p class="section-description">{{ 'eventRegistration.eventClassesDesc' | translate }}</p>

        <div *ngIf="eventClasses.length === 0" class="empty-state">
          <p>{{ 'eventRegistration.noClasses' | translate }}</p>
        </div>

        <!-- Classes grouped by day -->
        <div class="class-list">
          @for (dateGroup of getClassesByDate(); track dateGroup.date) {
            <div class="classes-day-group">
              <div class="classes-day-header">
                <i class="fas fa-calendar-day"></i>
                {{ formatScheduleDate(dateGroup.date) }}
              </div>
              @for (eventClass of dateGroup.classes; track eventClass.event_class_id) {
                <div class="class-card">
                  <div class="class-card-content">
                    <div class="class-info">
                      <div class="class-header">
                        <h4>{{ eventClass.class_name }}</h4>
                      </div>
                      <p class="class-description">{{ eventClass.class_description }}</p>

                      <div class="class-details">
                        <div class="detail-item" *ngIf="eventClass.max_num_registrations">
                          <i class="fas fa-users"></i>
                          <span>{{ eventClass.event_class_registrations?.length || 0 }} / {{ eventClass.max_num_registrations }} {{ 'eventRegistration.registered' | translate }}</span>
                        </div>
                        <div class="detail-item" *ngIf="eventClass.req_horse_registration_id">
                          <i class="fas fa-certificate"></i>
                          <span>{{ 'eventRegistration.requiresSpecificRegistry' | translate }}</span>
                        </div>
                        <div class="detail-item" *ngIf="eventClass.start_date_time">
                          <i class="fas fa-clock"></i>
                          <span>{{ eventClass.start_date_time | date:'shortTime' }}</span>
                        </div>
                      </div>
                    </div>

                    <div class="class-actions">
                      <div class="price-row">
                        <div class="class-price">
                          @if (!eventClass.price || eventClass.price === 0) {
                            <span class="free-badge">{{ 'eventRegistration.free' | translate }}</span>
                          } @else if (hasMemberDiscount('class', eventClass.event_class_id)) {
                            <span class="original-price">{{ eventClass.price }} {{ eventClass.price_currency }}</span>
                            <span class="discounted-price">{{ getEffectivePrice('class', eventClass.event_class_id, eventClass.price || 0) }} {{ eventClass.price_currency }}</span>
                            <span class="savings-badge">{{ getSavingsPercent('class', eventClass.event_class_id) }}% {{ 'eventRegistration.off' | translate }}</span>
                          } @else {
                            {{ eventClass.price }} {{ eventClass.price_currency }}
                          }
                        </div>
                        <button
                          class="btn-add-class-small"
                          (click)="addClassRegistration(eventClass)"
                          [disabled]="isClassFull(eventClass)">
                          <i class="fas fa-plus"></i>
                          {{ isClassFull(eventClass) ? ('eventRegistration.full' | translate) : ('eventRegistration.add' | translate) }}
                        </button>
                      </div>
                      @if (canGetMemberDiscount('class', eventClass.event_class_id)) {
                        <div class="member-offer">
                          <span class="member-price">{{ getBestMemberPrice('class', eventClass.event_class_id) }} {{ eventClass.price_currency }} {{ 'eventRegistration.forMembers' | translate }} ({{ getBestMemberSavings('class', eventClass.event_class_id) }}% {{ 'eventRegistration.off' | translate }})</span>
                          <button class="btn-become-member" (click)="openMembershipPurchaseModal('class', eventClass.event_class_id, eventClass.class_name, eventClass.price || 0, eventClass.price_currency || event?.default_currency || 'NOK'); $event.stopPropagation()">
                            {{ 'eventRegistration.becomeMember' | translate }}
                          </button>
                        </div>
                      }
                    </div>
                  </div>
                  @if (isMembershipRequired(eventClass) && !hasRequiredMembership(eventClass)) {
                    <div class="membership-warning">
                      <div class="membership-warning-header">
                        <i class="fas fa-lock"></i>
                        <span class="membership-warning-title">{{ 'eventRegistration.membershipRequired' | translate }}</span>
                      </div>
                      <p class="membership-warning-desc">{{ 'eventRegistration.membershipRequiredDesc' | translate }}</p>
                      <div class="membership-warning-plans">
                        <span class="membership-warning-plans-label">{{ 'eventRegistration.acceptedPlans' | translate }}:</span>
                        @for (plan of eventClass.accepted_membership_plans; track plan.plan_id) {
                          <span class="membership-plan-badge">{{ plan.name }}</span>
                        }
                      </div>
                      <button class="btn-get-required-membership" (click)="openRequiredMembershipPurchase(eventClass); $event.stopPropagation()">
                        <i class="fas fa-id-card"></i> {{ 'eventRegistration.getMembership' | translate }}
                      </button>
                    </div>
                  }
                </div>
              }
            </div>
          }
        </div>
      </div>

      <!-- Event Services -->
      <div class="section">
        <h3>{{ 'eventRegistration.eventServices' | translate }}</h3>
        <p class="section-description">{{ 'eventRegistration.eventServicesDesc' | translate }}</p>

        <div *ngIf="eventServices.length === 0" class="empty-state">
          <p>{{ 'eventRegistration.noServices' | translate }}</p>
        </div>

        <div class="service-list">
          <div *ngFor="let eventService of eventServices" class="service-card">
            <div class="service-card-content">
              <div class="service-info">
                <div class="service-header">
                  <h4>{{ eventService.event_service_name }}</h4>
                </div>
                <p class="service-description">{{ eventService.event_service_description }}</p>

                <div class="service-details">
                  <div class="detail-item" *ngIf="eventService.max_num_registrations">
                    <i class="fas fa-users"></i>
                    <span>{{ getAvailableServiceSlots(eventService) }} {{ 'eventRegistration.slotsAvailable' | translate }}</span>
                  </div>
                </div>
              </div>

              <div class="service-actions">
                <div class="price-row">
                  <div class="service-price">
                    @if (!eventService.price || eventService.price === 0) {
                      <span class="free-badge">{{ 'eventRegistration.free' | translate }}</span>
                    } @else if (hasMemberDiscount('service', eventService.event_service_id)) {
                      <span class="original-price">{{ eventService.price }} {{ eventService.price_currency }}</span>
                      <span class="discounted-price">{{ getEffectivePrice('service', eventService.event_service_id, eventService.price || 0) }} {{ eventService.price_currency }}</span>
                      <span class="savings-badge">{{ getSavingsPercent('service', eventService.event_service_id) }}% {{ 'eventRegistration.off' | translate }}</span>
                    } @else {
                      {{ eventService.price }} {{ eventService.price_currency }}
                    }
                  </div>
                  <button
                    class="btn-add-service-small"
                    (click)="addServiceRegistration(eventService)"
                    [disabled]="isServiceFull(eventService)">
                    <i class="fas fa-plus"></i>
                    {{ isServiceFull(eventService) ? ('eventRegistration.full' | translate) : ('eventRegistration.add' | translate) }}
                  </button>
                </div>
                @if (canGetMemberDiscount('service', eventService.event_service_id)) {
                  <div class="member-offer">
                    <span class="member-price">{{ getBestMemberPrice('service', eventService.event_service_id) }} {{ eventService.price_currency }} {{ 'eventRegistration.forMembers' | translate }} ({{ getBestMemberSavings('service', eventService.event_service_id) }}% {{ 'eventRegistration.off' | translate }})</span>
                    <button class="btn-become-member" (click)="openMembershipPurchaseModal('service', eventService.event_service_id, eventService.event_service_name, eventService.price || 0, eventService.price_currency || event?.default_currency || 'NOK'); $event.stopPropagation()">
                      {{ 'eventRegistration.becomeMember' | translate }}
                    </button>
                  </div>
                }
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Right Column: Registration Summary -->
    <div class="right-column">
      <div class="summary-card sticky">
        <h3>{{ 'eventRegistration.registrationSummary' | translate }}</h3>

        <!-- Class Registrations -->
        <div class="summary-section" *ngIf="classRegistrations.length > 0">
          <h4>{{ 'eventRegistration.classRegistrations' | translate }} ({{ classRegistrations.length }})</h4>

          <div *ngFor="let reg of classRegistrations; let i = index" class="registration-item" [class.collapsed]="!reg.expanded">
            <div class="registration-header" (click)="toggleClassRegistration(reg)">
              <div class="header-content">
                <i class="fas" [class.fa-chevron-down]="reg.expanded" [class.fa-chevron-right]="!reg.expanded"></i>
                <span class="registration-name">{{ reg.class_name }}</span>
              </div>
              <button class="btn-remove" (click)="removeClassRegistration(i); $event.stopPropagation()">
                <i class="fas fa-times"></i>
              </button>
            </div>

            <!-- Collapsed Summary -->
            <div class="collapsed-summary" *ngIf="!reg.expanded">
              <div class="summary-line compact">
                <span class="summary-text">{{ reg.user_name || ('eventRegistration.noRider' | translate) }} • {{ reg.horse_name || ('eventRegistration.noHorse' | translate) }}</span>
                <span class="value price">{{ reg.price }} {{ reg.price_currency }}</span>
              </div>
            </div>

            <!-- Expanded Details -->
            <div class="expanded-details" *ngIf="reg.expanded">
              <!-- User Selection -->
              <div class="form-group">
                <label>{{ 'eventRegistration.registerFor' | translate }} *</label>
                <select
                  [(ngModel)]="reg.user_id"
                  (change)="onUserSelected(reg, reg.user_id)"
                  class="form-control">
                  <option *ngFor="let member of familyMembers" [value]="member.user_id">
                    {{ member.name }}
                  </option>
                </select>
              </div>

              <!-- Horse Selection -->
              <div class="form-group">
                <label>{{ 'eventRegistration.selectHorse' | translate }} *</label>

                <!-- Show selected registry horse if not in user's horses -->
                <div class="selected-registry-horse" *ngIf="reg.horse_id && reg.horse_name && !isMyHorseById(reg.horse_id)">
                  <div class="selected-horse-info">
                    <i class="fas fa-horse"></i>
                    <span>{{ reg.horse_name }}</span>
                    <span class="registry-badge">{{ 'eventRegistration.fromRegistry' | translate }}</span>
                  </div>
                  <button type="button" class="btn-change-horse" (click)="clearHorseSelection(reg)">
                    <i class="fas fa-times"></i>
                  </button>
                </div>

                <!-- Show dropdown for user's horses or if no registry horse selected -->
                <div class="horse-selection-wrapper" *ngIf="!reg.horse_id || isMyHorseById(reg.horse_id)">
                  <select
                    [(ngModel)]="reg.horse_id"
                    (ngModelChange)="onHorseSelected(reg, $event)"
                    class="form-control"
                    [class.is-invalid]="reg.error">
                    <option [ngValue]="null">{{ 'eventRegistration.selectHorsePlaceholder' | translate }}</option>
                    <option *ngFor="let horse of allHorses" [ngValue]="horse.horse_id">
                      {{ horse.name }}
                    </option>
                  </select>
                  <button type="button" class="btn-horse-registry" (click)="openHorseRegistryModal(reg)">
                    <i class="fas fa-search"></i> {{ 'eventRegistration.horseRegistry' | translate }}
                  </button>
                </div>

                <div class="error-message" *ngIf="reg.error">
                  {{ reg.error }}
                </div>
              </div>

              <!-- Notes -->
              <div class="form-group">
                <label>{{ 'eventRegistration.notes' | translate }}</label>
                <textarea
                  [(ngModel)]="reg.notes"
                  class="form-control"
                  rows="2"
                  [placeholder]="'eventRegistration.notesPlaceholder' | translate"
                ></textarea>
              </div>

              <div class="registration-footer">
                <span class="price">{{ reg.price }} {{ reg.price_currency }}</span>
              </div>
            </div>
          </div>

          <button class="btn-add-horse" (click)="openAddHorseModal()">
            <i class="fas fa-plus"></i> {{ 'eventRegistration.addNewHorse' | translate }}
          </button>
        </div>

        <!-- Package Registrations (for training events) -->
        <div class="summary-section" *ngIf="packageRegistrations.length > 0">
          <h4>{{ 'eventRegistration.packageRegistrations' | translate }} ({{ packageRegistrations.length }})</h4>

          <div *ngFor="let reg of packageRegistrations; let i = index" class="registration-item" [class.collapsed]="!reg.expanded">
            <div class="registration-header" (click)="togglePackageRegistration(reg)">
              <div class="header-content">
                <i class="fas" [class.fa-chevron-down]="reg.expanded" [class.fa-chevron-right]="!reg.expanded"></i>
                <span class="registration-name">{{ reg.package_name }}</span>
              </div>
              <button class="btn-remove" (click)="removePackageRegistration(i); $event.stopPropagation()">
                <i class="fas fa-times"></i>
              </button>
            </div>

            <!-- Collapsed Summary -->
            <div class="collapsed-summary" *ngIf="!reg.expanded">
              <div class="summary-line compact">
                <span class="summary-text">{{ reg.user_name || ('eventRegistration.noRider' | translate) }} • {{ reg.session_count }} {{ 'eventRegistration.sessions' | translate }}</span>
                <span class="value price">{{ reg.price }} {{ reg.price_currency }}</span>
              </div>
            </div>

            <!-- Expanded Details -->
            <div class="expanded-details" *ngIf="reg.expanded">
              <!-- User Selection -->
              <div class="form-group">
                <label>{{ 'eventRegistration.registerFor' | translate }} *</label>
                <select
                  [(ngModel)]="reg.user_id"
                  (change)="onPackageUserSelected(reg, reg.user_id)"
                  class="form-control">
                  <option *ngFor="let member of familyMembers" [value]="member.user_id">
                    {{ member.name }}
                  </option>
                </select>
              </div>

              <!-- Horse Selection (Optional) -->
              <div class="form-group">
                <label>{{ 'eventRegistration.selectHorseOptional' | translate }}</label>

                <!-- Show selected registry horse if not in user's horses -->
                <div class="selected-registry-horse" *ngIf="reg.horse_id && reg.horse_name && !isMyHorseById(reg.horse_id)">
                  <div class="selected-horse-info">
                    <i class="fas fa-horse"></i>
                    <span>{{ reg.horse_name }}</span>
                    <span class="registry-badge">{{ 'eventRegistration.fromRegistry' | translate }}</span>
                  </div>
                  <button type="button" class="btn-change-horse" (click)="clearHorseSelection(reg)">
                    <i class="fas fa-times"></i>
                  </button>
                </div>

                <!-- Show dropdown for user's horses or if no registry horse selected -->
                <div class="horse-selection-wrapper" *ngIf="!reg.horse_id || isMyHorseById(reg.horse_id)">
                  <select
                    [(ngModel)]="reg.horse_id"
                    (ngModelChange)="onPackageHorseSelected(reg, $event)"
                    class="form-control">
                    <option [ngValue]="null">{{ 'eventRegistration.noHorseSelected' | translate }}</option>
                    <option *ngFor="let horse of allHorses" [ngValue]="horse.horse_id">
                      {{ horse.name }}
                    </option>
                  </select>
                  <button type="button" class="btn-horse-registry" (click)="openHorseRegistryModal(reg)">
                    <i class="fas fa-search"></i> {{ 'eventRegistration.horseRegistry' | translate }}
                  </button>
                </div>
              </div>

              <div class="package-summary">
                <div class="summary-row">
                  <span class="label">{{ 'eventRegistration.sessionsLabel' | translate }}:</span>
                  <span class="value">{{ reg.session_count }}</span>
                </div>
                <div class="summary-row">
                  <span class="label">{{ 'eventRegistration.typeLabel' | translate }}:</span>
                  <span class="value">{{ getSessionTypeLabel(reg.session_type) }}</span>
                </div>
              </div>

              <!-- Notes -->
              <div class="form-group">
                <label>{{ 'eventRegistration.notes' | translate }}</label>
                <textarea
                  [(ngModel)]="reg.notes"
                  class="form-control"
                  rows="2"
                  [placeholder]="'eventRegistration.notesPlaceholder' | translate"
                ></textarea>
              </div>

              <div class="registration-footer">
                <span class="price">{{ reg.price }} {{ reg.price_currency }}</span>
              </div>
            </div>
          </div>

          <button class="btn-add-horse" (click)="openAddHorseModal()">
            <i class="fas fa-plus"></i> {{ 'eventRegistration.addNewHorse' | translate }}
          </button>
        </div>

        <!-- Service Registrations -->
        <div class="summary-section" *ngIf="serviceRegistrations.length > 0">
          <h4>{{ 'eventRegistration.serviceRegistrations' | translate }} ({{ serviceRegistrations.length }})</h4>

          <div *ngFor="let reg of serviceRegistrations; let i = index" class="registration-item" [class.collapsed]="!reg.expanded">
            <div class="registration-header" (click)="toggleServiceRegistration(reg)">
              <div class="header-content">
                <i class="fas" [class.fa-chevron-down]="reg.expanded" [class.fa-chevron-right]="!reg.expanded"></i>
                <span class="registration-name">{{ reg.service_name }}</span>
              </div>
              <button class="btn-remove" (click)="removeServiceRegistration(i); $event.stopPropagation()">
                <i class="fas fa-times"></i>
              </button>
            </div>

            <!-- Collapsed Summary -->
            <div class="collapsed-summary" *ngIf="!reg.expanded">
              <div class="summary-line compact">
                <span class="summary-text">{{ 'eventRegistration.quantity' | translate }}: {{ reg.num_registrations }}</span>
                <span class="value price">{{ reg.price_per_unit * reg.num_registrations }} {{ reg.price_currency }}</span>
              </div>
            </div>

            <!-- Expanded Details -->
            <div class="expanded-details" *ngIf="reg.expanded">
              <div class="form-group">
                <label>{{ 'eventRegistration.quantity' | translate }}</label>
                <input
                  type="number"
                  min="1"
                  [max]="reg.max_slots || 999"
                  [(ngModel)]="reg.num_registrations"
                  (change)="updateServiceQuantity(reg, reg.num_registrations)"
                  class="form-control" />
                <small class="text-muted" *ngIf="reg.max_slots">
                  {{ 'eventRegistration.maxAvailable' | translate }}: {{ reg.max_slots }}
                </small>
              </div>

              <!-- Notes -->
              <div class="form-group">
                <label>{{ 'eventRegistration.notes' | translate }}</label>
                <textarea
                  [(ngModel)]="reg.notes"
                  class="form-control"
                  rows="2"
                  [placeholder]="'eventRegistration.notesPlaceholder' | translate"
                ></textarea>
              </div>

              <div class="registration-footer">
                <span class="price">{{ reg.price_per_unit * reg.num_registrations }} {{ reg.price_currency }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Empty State -->
        <div *ngIf="classRegistrations.length === 0 && serviceRegistrations.length === 0 && packageRegistrations.length === 0" class="empty-summary">
          <i class="fas fa-clipboard-list"></i>
          <p>{{ 'eventRegistration.noRegistrationsYet' | translate }}</p>
          <p class="empty-subtitle" *ngIf="isTrainingEvent">{{ 'eventRegistration.addPackagesOrServices' | translate }}</p>
          <p class="empty-subtitle" *ngIf="!isTrainingEvent">{{ 'eventRegistration.addClassesOrServices' | translate }}</p>
        </div>

        <!-- Total -->
        <div class="summary-total" *ngIf="classRegistrations.length > 0 || serviceRegistrations.length > 0 || packageRegistrations.length > 0">
          <div class="total-row">
            <span class="total-label">{{ 'eventRegistration.total' | translate }}:</span>
            <span class="total-price">{{ getTotalPrice() }} {{ getCurrency() }}</span>
          </div>
        </div>

        <!-- Terms Agreement -->
        <div class="terms-agreement" *ngIf="classRegistrations.length > 0 || serviceRegistrations.length > 0 || packageRegistrations.length > 0">
          <div class="terms-link-card">
            <i class="fas fa-file-contract"></i>
            <div class="terms-text">
              <p>{{ 'eventRegistration.byRegistering' | translate }}</p>
              <a (click)="openTermsModal()" class="terms-link">{{ 'eventRegistration.termsAndConditions' | translate }}</a>
            </div>
          </div>
        </div>

        <!-- Membership Alert -->
        @if (hasMembershipIssues) {
          <div class="membership-alert">
            <div class="membership-alert-header">
              <i class="fas fa-exclamation-triangle"></i>
              <span class="membership-alert-title">{{ 'eventRegistration.membershipRequired' | translate }}</span>
            </div>
            <p class="membership-alert-desc">{{ 'eventRegistration.membershipMissing' | translate }}</p>
            @for (reg of getMissingMembershipClasses(); track reg.event_class_id) {
              <div class="membership-alert-item">
                <span class="membership-alert-class-name">{{ reg.class_name }}</span>
                <div class="membership-alert-plans">
                  @for (plan of (getClassById(reg.event_class_id)?.accepted_membership_plans || []); track plan.plan_id) {
                    <span class="membership-plan-badge">{{ plan.name }}</span>
                  }
                </div>
              </div>
            }
            <p class="membership-alert-footer">{{ 'eventRegistration.resolveMembership' | translate }}</p>
          </div>
        }

        <!-- Submit Button -->
        <button
          class="btn-submit"
          (click)="submitRegistrations()"
          [disabled]="!canSubmit()">
          <span *ngIf="!isSubmitting">
            <i class="fas fa-check"></i> {{ 'eventRegistration.completeRegistration' | translate }}
          </span>
          <span *ngIf="isSubmitting">
            <i class="fas fa-spinner fa-spin"></i> {{ 'eventRegistration.processing' | translate }}
          </span>
        </button>
      </div>
    </div>
  </div>

  <!-- Sponsors Section -->
  <div class="sponsors-section" *ngIf="eventSponsors && eventSponsors.length > 0">
    <h3>{{ 'eventRegistration.proudlySponsoredBy' | translate }}</h3>
    <div class="sponsors-marquee-container">
      <div class="sponsors-marquee">
        <div class="sponsors-track" [class.animate]="shouldAnimateSponsors()" [class.static]="!shouldAnimateSponsors()">
          <div *ngFor="let eventSponsor of eventSponsors" class="sponsor-slide">
            <div class="sponsor-card-animated" *ngIf="eventSponsor.sponsor">
              <div class="sponsor-badge">
                <img *ngIf="eventSponsor.sponsor.logo_url"
                     [src]="eventSponsor.sponsor.logo_url"
                     [alt]="eventSponsor.sponsor.sponsor_name"
                     class="sponsor-logo-animated" />
                <div class="sponsor-shine"></div>
              </div>
              <div class="sponsor-details">
                <h4>{{ eventSponsor.sponsor.sponsor_name }}</h4>
                <p *ngIf="eventSponsor.sponsor.sponsor_description">{{ eventSponsor.sponsor.sponsor_description }}</p>
              </div>
            </div>
          </div>
          <!-- Duplicate for seamless loop (only when animating) -->
          <ng-container *ngIf="shouldAnimateSponsors()">
            <div *ngFor="let eventSponsor of eventSponsors" class="sponsor-slide">
              <div class="sponsor-card-animated" *ngIf="eventSponsor.sponsor">
                <div class="sponsor-badge">
                  <img *ngIf="eventSponsor.sponsor.logo_url"
                       [src]="eventSponsor.sponsor.logo_url"
                       [alt]="eventSponsor.sponsor.sponsor_name"
                       class="sponsor-logo-animated" />
                  <div class="sponsor-shine"></div>
                </div>
                <div class="sponsor-details">
                  <h4>{{ eventSponsor.sponsor.sponsor_name }}</h4>
                  <p *ngIf="eventSponsor.sponsor.sponsor_description">{{ eventSponsor.sponsor.sponsor_description }}</p>
                </div>
              </div>
            </div>
          </ng-container>
        </div>
      </div>
    </div>
  </div>
</div>

<!-- Add Horse Modal -->
<div class="modal-overlay" *ngIf="showAddHorseModal" (click)="closeAddHorseModal()">
  <div class="modal-content modal-content-large" (click)="$event.stopPropagation()">
    <div class="modal-header">
      <h3>{{ 'eventRegistration.addNewHorse' | translate }}</h3>
      <button class="btn-close" (click)="closeAddHorseModal()">
        <i class="fas fa-times"></i>
      </button>
    </div>

    <form [formGroup]="addHorseForm" class="modal-body">
      <!-- Base Info Section -->
      <div class="form-section">
        <h4 class="section-title">{{ 'horses.form.baseInfo' | translate }}</h4>
        <p class="section-subtitle">{{ 'horses.form.requiredFieldsPrefix' | translate }} <span class="required-asterisk">*</span> {{ 'horses.form.requiredFieldsSuffix' | translate }}</p>
      </div>

      <div class="form-group">
        <label>{{ 'eventRegistration.horseName' | translate }} *</label>
        <input type="text" formControlName="name" class="form-control"
               [class.is-invalid]="addHorseSubmitted && addHorseForm.get('name')?.invalid"
               [placeholder]="'eventRegistration.enterHorseName' | translate" />
      </div>

      <div class="form-row">
        <div class="form-group half">
          <label>{{ 'eventRegistration.gender' | translate }} *</label>
          <select formControlName="gender" class="form-control"
                  [class.is-invalid]="addHorseSubmitted && addHorseForm.get('gender')?.invalid">
            <option [ngValue]="null">{{ 'eventRegistration.selectGender' | translate }}</option>
            <option *ngFor="let gender of horseGenders" [ngValue]="gender.id">
              {{ gender.name }}
            </option>
          </select>
        </div>
        <div class="form-group half">
          <label>{{ 'eventRegistration.dateOfBirth' | translate }} *</label>
          <input type="date" formControlName="date_of_birth" class="form-control"
                 [class.is-invalid]="addHorseSubmitted && addHorseForm.get('date_of_birth')?.invalid" />
        </div>
      </div>

      <div class="form-row">
        <div class="form-group half">
          <label>{{ 'eventRegistration.breed' | translate }}</label>
          <input type="text" formControlName="breed" class="form-control" [placeholder]="'eventRegistration.enterBreed' | translate" />
        </div>
        <div class="form-group half">
          <label>{{ 'eventRegistration.color' | translate }}</label>
          <input type="text" formControlName="color" class="form-control" [placeholder]="'eventRegistration.enterColor' | translate" />
        </div>
      </div>

      <!-- Registration Section -->
      <div class="form-section">
        <h4 class="section-title">{{ 'horses.form.registration' | translate }}</h4>
      </div>

      <div class="form-row">
        <div class="form-group half">
          <label>{{ 'eventRegistration.passportId' | translate }} *</label>
          <input type="text" formControlName="passport_id" class="form-control"
                 [class.is-invalid]="addHorseSubmitted && addHorseForm.get('passport_id')?.invalid"
                 [placeholder]="'eventRegistration.enterPassportId' | translate" />
        </div>
        <div class="form-group half">
          <label>{{ 'eventRegistration.horseRegistry' | translate }}</label>
          <select formControlName="horse_registry_id" class="form-control">
            <option [ngValue]="null">{{ 'eventRegistration.selectRegistry' | translate }}</option>
            <option *ngFor="let registry of horseRegistries" [ngValue]="registry.registry_id">
              {{ registry.registry_name }}
            </option>
          </select>
        </div>
      </div>

      <!-- Vaccination Section -->
      <div class="form-section">
        <h4 class="section-title">{{ 'horses.form.vaccination' | translate }}</h4>
      </div>

      <div class="form-row">
        <div class="form-group half">
          <label>{{ 'horses.form.vaccinationDate' | translate }}</label>
          <input type="date" formControlName="vaccination_date" class="form-control" />
        </div>
        <div class="form-group half">
          <label>{{ 'horses.form.nextVaccinationDate' | translate }}</label>
          <input type="date" formControlName="next_vaccination_date" class="form-control" />
        </div>
      </div>

      <!-- Images Section -->
      <div class="form-section">
        <h4 class="section-title">{{ 'horses.form.images' | translate }}</h4>
      </div>

      <div class="image-upload-row">
        <div class="image-upload-col">
          <label>{{ 'horses.form.horseImage' | translate }}</label>
          <div class="image-upload-box" *ngIf="!horseImagePreview">
            <input type="file" accept="image/*" (change)="onHorseImageSelected($event)" class="file-input" />
            <div class="upload-placeholder">
              <i class="fas fa-camera"></i>
              <span>{{ 'common.upload' | translate }}</span>
            </div>
          </div>
          <div class="image-preview-box" *ngIf="horseImagePreview">
            <img [src]="horseImagePreview" alt="Horse preview" />
            <button type="button" class="btn-remove-preview" (click)="removeHorseImage()">
              <i class="fas fa-times"></i>
            </button>
          </div>
        </div>

        <div class="image-upload-col">
          <label>{{ 'horses.form.pedigree' | translate }}</label>
          <div class="image-upload-box" *ngIf="!pedigreePreview">
            <input type="file" accept="image/*" (change)="onPedigreeSelected($event)" class="file-input" />
            <div class="upload-placeholder">
              <i class="fas fa-file-alt"></i>
              <span>{{ 'common.upload' | translate }}</span>
            </div>
          </div>
          <div class="image-preview-box" *ngIf="pedigreePreview">
            <img [src]="pedigreePreview" alt="Pedigree preview" />
            <button type="button" class="btn-remove-preview" (click)="removePedigree()">
              <i class="fas fa-times"></i>
            </button>
          </div>
        </div>

        <div class="image-upload-col">
          <label>{{ 'horses.form.vaccinationCard' | translate }}</label>
          <div class="image-upload-box" *ngIf="!vaccinationCardPreview">
            <input type="file" accept="image/*" (change)="onVaccinationCardSelected($event)" class="file-input" />
            <div class="upload-placeholder">
              <i class="fas fa-syringe"></i>
              <span>{{ 'common.upload' | translate }}</span>
            </div>
          </div>
          <div class="image-preview-box" *ngIf="vaccinationCardPreview">
            <img [src]="vaccinationCardPreview" alt="Vaccination card preview" />
            <button type="button" class="btn-remove-preview" (click)="removeVaccinationCard()">
              <i class="fas fa-times"></i>
            </button>
          </div>
        </div>
      </div>
    </form>

    <div class="modal-footer">
      <button class="btn-cancel" (click)="closeAddHorseModal()">{{ 'common.cancel' | translate }}</button>
      <button class="btn-save" (click)="submitNewHorse()">
        <i class="fas fa-plus"></i> {{ 'eventRegistration.addHorse' | translate }}
      </button>
    </div>
  </div>
</div>

<!-- Terms Modal -->
<div class="modal-overlay" *ngIf="showTermsModal" (click)="closeTermsModal()">
  <div class="modal-content modal-content-large" (click)="$event.stopPropagation()">
    <div class="modal-header">
      <h3>{{ 'eventRegistration.termsTitle' | translate }}</h3>
      <button class="btn-close" (click)="closeTermsModal()">
        <i class="fas fa-times"></i>
      </button>
    </div>

    <div class="modal-body terms-modal-body">
      <div class="terms-content">
        <h4>{{ 'eventRegistration.terms.section1Title' | translate }}</h4>
        <ul>
          <li>{{ 'eventRegistration.terms.section1Item1' | translate }}</li>
          <li>{{ 'eventRegistration.terms.section1Item2' | translate }}</li>
          <li>{{ 'eventRegistration.terms.section1Item3' | translate }}</li>
        </ul>

        <h4>{{ 'eventRegistration.terms.section2Title' | translate }}</h4>
        <ul>
          <li>{{ 'eventRegistration.terms.section2Item1' | translate }}</li>
          <li>{{ 'eventRegistration.terms.section2Item2' | translate }}</li>
          <li>{{ 'eventRegistration.terms.section2Item3' | translate }}</li>
        </ul>

        <h4>{{ 'eventRegistration.terms.section3Title' | translate }}</h4>
        <ul>
          <li>{{ 'eventRegistration.terms.section3Item1' | translate }}</li>
          <li>{{ 'eventRegistration.terms.section3Item2' | translate }}</li>
          <li>{{ 'eventRegistration.terms.section3Item3' | translate }}</li>
        </ul>

        <h4>{{ 'eventRegistration.terms.section4Title' | translate }}</h4>
        <ul>
          <li>{{ 'eventRegistration.terms.section4Item1' | translate }}</li>
          <li>{{ 'eventRegistration.terms.section4Item2' | translate }}</li>
          <li>{{ 'eventRegistration.terms.section4Item3' | translate }}</li>
        </ul>

        <h4>{{ 'eventRegistration.terms.section5Title' | translate }}</h4>
        <ul>
          <li>{{ 'eventRegistration.terms.section5Item1' | translate }}</li>
          <li>{{ 'eventRegistration.terms.section5Item2' | translate }}</li>
          <li>{{ 'eventRegistration.terms.section5Item3' | translate }}</li>
        </ul>

        <h4>{{ 'eventRegistration.terms.section6Title' | translate }}</h4>
        <ul>
          <li>{{ 'eventRegistration.terms.section6Item1' | translate }}</li>
          <li>{{ 'eventRegistration.terms.section6Item2' | translate }}</li>
          <li>{{ 'eventRegistration.terms.section6Item3' | translate }}</li>
        </ul>
      </div>
    </div>

    <div class="modal-footer">
      <button class="btn-save" (click)="closeTermsModal()">
        <i class="fas fa-check"></i> {{ 'eventRegistration.iUnderstand' | translate }}
      </button>
    </div>
  </div>
</div>

<!-- Horse Registry Search Modal -->
<div class="modal-overlay" *ngIf="showHorseRegistryModal" (click)="closeHorseRegistryModal()">
  <div class="modal-content modal-content-large" (click)="$event.stopPropagation()">
    <div class="modal-header">
      <h3>{{ 'eventRegistration.horseRegistry' | translate }}</h3>
      <button class="btn-close" (click)="closeHorseRegistryModal()">
        <i class="fas fa-times"></i>
      </button>
    </div>

    <div class="modal-body">
      <p class="search-description">{{ 'eventRegistration.horseRegistryDescription' | translate }}</p>

      <div class="search-input-wrapper">
        <input
          type="text"
          [(ngModel)]="horseSearchQuery"
          (input)="searchHorseRegistry()"
          class="form-control search-input"
          [placeholder]="'eventRegistration.searchHorsePlaceholder' | translate"
        />
        <i class="fas fa-search search-icon"></i>
      </div>

      <div *ngIf="isSearchingHorses" class="search-loading">
        <i class="fas fa-spinner fa-spin"></i> {{ 'common.searching' | translate }}
      </div>

      <div *ngIf="!isSearchingHorses && horseSearchQuery.length >= 2 && horseSearchResults.length === 0" class="search-no-results">
        <i class="fas fa-horse"></i>
        <p>{{ 'eventRegistration.noHorsesFound' | translate }}</p>
      </div>

      <div class="horse-results-list" *ngIf="horseSearchResults.length > 0">
        <div *ngFor="let horse of horseSearchResults" class="horse-result-card" [class.my-horse]="isMyHorse(horse)">
          <div class="horse-result-info">
            <div class="horse-result-image">
              <img *ngIf="horse.image_url" [src]="horse.image_url" alt="{{ horse.name }}" />
              <div *ngIf="!horse.image_url" class="horse-placeholder">
                <i class="fas fa-horse"></i>
              </div>
            </div>
            <div class="horse-result-details">
              <h4>{{ horse.name }}</h4>
              <div class="horse-meta">
                <span *ngIf="horse.passport_id"><i class="fas fa-id-card"></i> {{ horse.passport_id }}</span>
                <span *ngIf="horse.breed"><i class="fas fa-dna"></i> {{ horse.breed }}</span>
                <span *ngIf="isMyHorse(horse)" class="my-horse-badge"><i class="fas fa-user"></i> {{ 'eventRegistration.myHorse' | translate }}</span>
              </div>
            </div>
          </div>
          <button class="btn-select-horse" (click)="selectHorseFromRegistry(horse)">
            <i class="fas fa-check"></i> {{ 'eventRegistration.select' | translate }}
          </button>
        </div>
      </div>
    </div>

    <div class="modal-footer">
      <button class="btn-cancel" (click)="closeHorseRegistryModal()">{{ 'common.cancel' | translate }}</button>
    </div>
  </div>
</div>
